import { FC, useRef, useState } from 'react';
import IconButton from '@ui/IconButton';
import IconEllipsis from '@icons/IconEllipsis';
import { NotificationItemProps } from '@layouts/components/Header/components/NotificationPanel/types';
import clsx from 'clsx';
import List from '@ui/List';
import ListItem from '@ui/List/ListItem';
import { Transition } from '@headlessui/react';
import IconArchive from '@icons/IconArchive';
import IconDoneAll from '@icons/IconDoneAll';
import { useClickOutside } from '@hooks/useClickOutside';
import { useNotificationContext } from '@layouts/components/Header/components/NotificationPanel/context';

const relativeTimeFormatter = new Intl.RelativeTimeFormat(undefined, {
  numeric: 'auto'
});

const timeFormatter = new Intl.DateTimeFormat('en-GB', {
  hour: 'numeric',
  minute: 'numeric'
});

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  dateStyle: 'short'
});

const NotificationItem: FC<NotificationItemProps> = ({ notification }) => {
  const { setMarkForDelete, markAsRead, working } = useNotificationContext();

  const [optionsOpen, setOptionsOpen] = useState<boolean>(false);
  const optionsContainerRef = useRef<HTMLSpanElement>(null);

  useClickOutside([optionsContainerRef], () => {
    if (optionsOpen) setOptionsOpen(false);
  });

  const handleMarkAsRead = () => {
    markAsRead(notification.id);
    setOptionsOpen(false);
  };
  const handleDelete = () => {
    setMarkForDelete(notification.id);
    setOptionsOpen(false);
  };

  /**
   * Converts the date string to a relative printable date
   * @param sDate
   */
  const renderRelativeDate = (sDate: string) => {
    const date: Date = new Date(sDate);
    const today = new Date();
    // Calculate time different from now
    const diffTime: number = today.getTime() - date.getTime();
    // Calculate time different in days from now
    const diffDays: number = Math.round(diffTime / (1000 * 60 * 60 * 24));

    const time: string = ' at ' + timeFormatter.format(date);

    // If a week ago show time in 1 week ago format
    if (diffDays === -7) return relativeTimeFormatter.format(-1, 'week');
    // If more than a day show time in days ago and show time
    if (diffDays >= -1)
      return relativeTimeFormatter.format(-diffDays, 'day') + time;
    // If more than a week show time in days ago only
    if (diffDays > -7) return relativeTimeFormatter.format(-diffDays, 'day');

    // If other just show formatted date
    return dateFormatter.format(date);
  };

  const handleItemClick = () => {
    markAsRead(notification.id, () => {
      if (notification.data?.link) {
        window.location.href = notification.data.link;
      }
    });
  };

  return (
    <div
      className={
        'group flex w-full cursor-pointer items-start border-b border-solid border-gray-200 py-4 pl-6 pr-3 last:border-b-0 ' +
        clsx({
          'border-l-2 border-l-primary bg-primary-500/5 hover:bg-primary-500/10':
            !notification.read_at,
          'hover:bg-gray-50': notification.read_at
        })
      }
      onClick={handleItemClick}
    >
      <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-full border border-gray-200 bg-gray-50">
        <img
          alt="Notification"
          src={'/imgs/notification-placeholder.png'}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>
      <div className="flex-auto pl-4 pr-2 text-left">
        <p
          className={
            'mb-1.5 text-sm ' +
            clsx({
              'font-normal': notification.read_at,
              'font-semibold': !notification.read_at
            })
          }
        >
          {notification.data?.title}
        </p>
        <p className="mb-1.5 text-xs">{notification.data?.desc}</p>
        <p className="text-xs capitalize text-gray-500">
          {notification.created_at
            ? renderRelativeDate(notification.created_at)
            : ''}
        </p>
      </div>
      <div className="shrink-0 pt-4 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
        <span
          className="relative"
          ref={optionsContainerRef}
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            size="small"
            onClick={() => setOptionsOpen(!optionsOpen)}
            loading={working === notification.id}
          >
            <IconEllipsis />
          </IconButton>
          <Transition
            show={optionsOpen && working !== notification.id}
            enter="transition duration-200 ease-out"
            enterFrom="opacity-0 scale-0"
            enterTo="opacity-100 scale-100"
            leave="transition duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-0"
            className="absolute -top-4 right-8 z-dropdown origin-top-right whitespace-nowrap"
          >
            <List>
              {!notification.read_at && (
                <ListItem icon={<IconDoneAll />} onClick={handleMarkAsRead}>
                  Mark as read
                </ListItem>
              )}
              <ListItem icon={<IconArchive />} onClick={handleDelete}>
                Delete notifications
              </ListItem>
            </List>
          </Transition>
        </span>
      </div>
    </div>
  );
};

export default NotificationItem;
